/**
 * Phira's string tween for text events (F§9.3, R§3.6, D§6.8, E§4.13): `tween(x = start, y = end, t)` with t the
 * eased progress (f32). Cases, checked in this order:
 *
 *  1. counter  both strings contain `%P%`: every `%P%` is removed from both. t >= 1 -> y, t <= 0 -> x (stripped,
 *              verbatim). Otherwise both are parsed as f32 the way Rust's `str::parse::<f32>` does (the WHOLE
 *              stripped string must be a number, no whitespace; failure -> 0), v = x + (y - x) t in f32, and v is
 *              formatted with `{:.0}` when both parsed values are integral, else `{:.3}`.
 *  2. empty    stripped x and y both empty -> "".
 *  3. erase    y empty -> the first round((1 - t) len(x)) characters of x (stripped).
 *  4. reveal   x empty -> the first round(t len(y)) characters of y.
 *  5. extend   y starts with x -> x + the next floor((len(y) - len(x)) t) characters of y.
 *  6. shrink   x starts with y -> y + the next round((len(x) - len(y)) (1 - t)) characters of x.
 *  7. switch   otherwise -> x (stripped). There is no switch to y until the end keyframe.
 *
 * Lengths count Unicode scalar values; products are f32, `round` is Rust's half-away-from-zero, and the
 * results are cast `as usize` (saturating: negative and NaN -> 0). `%P%` is stripped from x for cases 2-7
 * (F§9.3 states it for erase and switch; one strip of x before the non-counter cases is the reading that covers
 * both); y keeps it.
 *
 * Hazards: cases 3 and 4 slice past the end when the eased progress overshoots (t > 1 for reveal, t < 0 for
 * erase) and Phira PANICS (F§9.3). `stringTween` clamps the slice so the preview can keep running; use
 * `stringTweenDetailed(...).panics` to detect the crash. Extend/shrink asking for more characters than exist is
 * clamped and reported as `clipped` (Phira's behaviour there is unverified, PH-16).
 *
 * Number formatting follows Rust's exact decimal formatting: the f32 value's exact binary expansion is rounded to
 * the requested number of decimals with ties to EVEN (Rust >= 1.67: `{:.0}` of 0.5 is "0", of 2.5 is "2", of
 * 3.5 is "4"; `{:.3}` of 0.0625 is "0.062"), unlike JavaScript's `toFixed`, which rounds ties up. A negative value
 * keeps its sign even when it rounds to zero ("-0", "-0.000"); NaN is "NaN", infinities are "inf" / "-inf".
 * Integral = finite with no fractional part.
 */

import { roundHalfAwayFromZero } from './bezierKey';

const fr = Math.fround;

const COUNTER_MARK = '%P%';

export type StringTweenCase = 'counter' | 'empty' | 'erase' | 'reveal' | 'extend' | 'shrink' | 'switch';

export interface StringTweenResult {
  readonly text: string;
  readonly case: StringTweenCase;
  /** Phira would panic here (reveal/erase sliced past the end); `text` is the clamped slice. */
  readonly panics: boolean;
  /** Extend/shrink asked for more characters than exist (clamped). */
  readonly clipped: boolean;
}

/** Phira's string tween (clamped where Phira would panic; see `stringTweenDetailed`). */
export function stringTween(start: string, end: string, t: number): string {
  return stringTweenDetailed(start, end, t).text;
}

export function stringTweenDetailed(x: string, y: string, t: number): StringTweenResult {
  const tt = fr(t);
  if (x.includes(COUNTER_MARK) && y.includes(COUNTER_MARK)) {
    const xs = stripCounterMarks(x);
    const ys = stripCounterMarks(y);
    if (tt >= 1) return result(ys, 'counter');
    if (tt <= 0) return result(xs, 'counter');
    const xv = parseRustF32(xs) ?? 0;
    const yv = parseRustF32(ys) ?? 0;
    const v = fr(xv + fr(fr(yv - xv) * tt));
    return result(formatRustFixed(v, isIntegral(xv) && isIntegral(yv) ? 0 : 3), 'counter');
  }
  const xs = stripCounterMarks(x);
  if (xs === '' && y === '') return result('', 'empty');
  if (y === '') {
    const chars = Array.from(xs);
    const n = asUsize(roundHalfAwayFromZero(fr(fr(1 - tt) * chars.length)));
    return sliced(chars, n, 'erase');
  }
  if (xs === '') {
    const chars = Array.from(y);
    const n = asUsize(roundHalfAwayFromZero(fr(tt * chars.length)));
    return sliced(chars, n, 'reveal');
  }
  if (y.startsWith(xs)) {
    const xc = Array.from(xs);
    const yc = Array.from(y);
    const avail = yc.length - xc.length;
    const k = asUsize(Math.floor(fr(fr(avail) * tt)));
    return {
      text: yc.slice(0, xc.length + Math.min(k, avail)).join(''),
      case: 'extend',
      panics: false,
      clipped: k > avail,
    };
  }
  if (xs.startsWith(y)) {
    const xc = Array.from(xs);
    const yc = Array.from(y);
    const avail = xc.length - yc.length;
    const k = asUsize(roundHalfAwayFromZero(fr(fr(avail) * fr(1 - tt))));
    return {
      text: xc.slice(0, yc.length + Math.min(k, avail)).join(''),
      case: 'shrink',
      panics: false,
      clipped: k > avail,
    };
  }
  return result(xs, 'switch');
}

function result(text: string, c: StringTweenCase): StringTweenResult {
  return { text, case: c, panics: false, clipped: false };
}

function sliced(chars: readonly string[], n: number, c: 'erase' | 'reveal'): StringTweenResult {
  return {
    text: chars.slice(0, Math.min(n, chars.length)).join(''),
    case: c,
    panics: n > chars.length,
    clipped: false,
  };
}

export function stripCounterMarks(s: string): string {
  return s.split(COUNTER_MARK).join('');
}

/** Rust `f32 as usize`: saturating, NaN -> 0. */
function asUsize(v: number): number {
  if (Number.isNaN(v) || v <= 0) return 0;
  return v >= Number.MAX_SAFE_INTEGER ? Number.MAX_SAFE_INTEGER : Math.trunc(v);
}

function isIntegral(v: number): boolean {
  return Number.isFinite(v) && Math.trunc(v) === v;
}

// ---------------------------------------------------------------------------------------------------------------
// Exact binary/decimal helpers
// ---------------------------------------------------------------------------------------------------------------

/** Exact decomposition |v| = mant * 2^exp2 of a finite double. */
function decompose(v: number): { readonly mant: bigint; readonly exp2: number } {
  const view = new DataView(new ArrayBuffer(8));
  view.setFloat64(0, Math.abs(v));
  const hi = view.getUint32(0);
  const lo = view.getUint32(4);
  const biased = (hi >>> 20) & 0x7ff;
  const frac = (BigInt(hi & 0xfffff) << 32n) | BigInt(lo);
  if (biased === 0) return { mant: frac, exp2: -1074 };
  return { mant: frac | (1n << 52n), exp2: biased - 1075 };
}

/**
 * Rust's `format!("{:.N}", v)` for a finite or non-finite value: exact decimal expansion rounded half to even.
 */
export function formatRustFixed(v: number, digits: number): string {
  if (Number.isNaN(v)) return 'NaN';
  if (v === Infinity) return 'inf';
  if (v === -Infinity) return '-inf';
  const negative = v < 0 || Object.is(v, -0);
  const { mant, exp2 } = decompose(v);
  let num = mant * 10n ** BigInt(digits);
  let den = 1n;
  if (exp2 >= 0) num <<= BigInt(exp2);
  else den <<= BigInt(-exp2);
  let q = num / den;
  const twice = (num % den) * 2n;
  if (twice > den || (twice === den && (q & 1n) === 1n)) q += 1n;
  let s = q.toString();
  if (digits > 0) {
    s = s.padStart(digits + 1, '0');
    s = `${s.slice(0, s.length - digits)}.${s.slice(s.length - digits)}`;
  }
  return negative ? `-${s}` : s;
}

const RUST_FLOAT = /^([+-]?)(?:(inf|infinity|nan)|(\d+)(?:\.(\d*))?(?:[eE]([+-]?\d+))?|\.(\d+)(?:[eE]([+-]?\d+))?)$/i;

const F32_MAX = fr(3.4028234663852886e38);
const TWO_POW_128 = 2 ** 128;

/**
 * Rust's `str::parse::<f32>()`: optional sign, `inf` / `infinity` / `nan` (any case), or a decimal with optional
 * fraction and exponent (at least one digit; no whitespace, no hex, no `_`). The decimal is rounded directly to
 * the nearest f32 (ties to even) — the f64 round trip is corrected where it would double-round. null = parse
 * error.
 */
export function parseRustF32(s: string): number | null {
  const m = RUST_FLOAT.exec(s);
  if (m === null) return null;
  const negative = m[1] === '-';
  const special = m[2];
  if (special !== undefined) {
    if (special.toLowerCase() === 'nan') return Number.NaN;
    return negative ? -Infinity : Infinity;
  }
  const intDigits = m[3] ?? '';
  const fracDigits = m[3] !== undefined ? (m[4] ?? '') : (m[6] ?? '');
  const expText = m[3] !== undefined ? m[5] : m[7];
  const exp10 = expText === undefined ? 0 : Number.parseInt(expText, 10);
  const magnitude = Math.abs(Number(s));
  const rounded = roundDecimalToF32(magnitude, intDigits + fracDigits, exp10 - fracDigits.length);
  return negative ? -rounded : rounded;
}

/** Correct decimal -> f32 rounding given the correctly rounded f64 `r` of the decimal digits * 10^exp. */
function roundDecimalToF32(r: number, digits: string, exp: number): number {
  const f = fr(r);
  if (!Number.isFinite(r) || f === r) return f;
  const lo = f < r ? f : nextDownF32(f);
  const hi = f > r ? f : nextUpF32(f);
  const hiValue = hi === Infinity ? TWO_POW_128 : hi;
  if ((lo + hiValue) / 2 !== r) return f;
  // r is exactly an f32 midpoint: the f64 rounding may have moved the decimal onto it. Decide exactly.
  const cmp = compareDecimalToDouble(digits, exp, r);
  if (cmp === 0) return f;
  return cmp > 0 ? hi : lo;
}

/** Sign of (digits * 10^exp - r) for a non-negative finite double r, computed exactly. */
function compareDecimalToDouble(digits: string, exp: number, r: number): number {
  let dNum = BigInt(digits === '' ? '0' : digits);
  let dDen = 1n;
  if (exp >= 0) dNum *= 10n ** BigInt(exp);
  else dDen = 10n ** BigInt(-exp);
  const { mant, exp2 } = decompose(r);
  let rNum = mant;
  let rDen = 1n;
  if (exp2 >= 0) rNum <<= BigInt(exp2);
  else rDen <<= BigInt(-exp2);
  const left = dNum * rDen;
  const right = rNum * dDen;
  return left > right ? 1 : left < right ? -1 : 0;
}

const F32_BITS = new Float32Array(1);
const U32_BITS = new Uint32Array(F32_BITS.buffer);

/** Next f32 above a non-negative finite f32 value (Infinity above F32_MAX). */
function nextUpF32(f: number): number {
  if (f >= F32_MAX) return Infinity;
  F32_BITS[0] = f;
  U32_BITS[0] = (U32_BITS[0] ?? 0) + 1;
  return F32_BITS[0];
}

/** Next f32 below a positive f32 value (F32_MAX below Infinity). */
function nextDownF32(f: number): number {
  if (f === Infinity) return F32_MAX;
  if (f <= 0) return 0;
  F32_BITS[0] = f;
  U32_BITS[0] = (U32_BITS[0] ?? 1) - 1;
  return F32_BITS[0];
}
